/**
 * ShiftOpenDialog — cashier opens a shift.
 *
 * Captures optional opening cash float. Default 0.
 *
 * Day 14 Sprint 1D #3 + #4.
 */
import { useState } from "react";
import { PlayCircle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useOpenShift } from "@/hooks/useShifts";
import { curSymbol } from "@/lib/currency";

interface ShiftOpenDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

export default function ShiftOpenDialog({
  open,
  onOpenChange,
}: ShiftOpenDialogProps) {
  const [openingCash, setOpeningCash] = useState("0");
  const [notes, setNotes] = useState("");
  const openShift = useOpenShift();

  function reset() {
    setOpeningCash("0");
    setNotes("");
  }
  function close(o: boolean) {
    if (!o) reset();
    onOpenChange(o);
  }

  async function onSubmit() {
    const cedis = Number(openingCash);
    if (!Number.isFinite(cedis) || cedis < 0) {
      toast.error("Opening cash must be a non-negative number");
      return;
    }
    try {
      const result = await openShift.mutateAsync({
        openingCashMinor: Math.round(cedis * 100),
        openingNotes: notes.trim() || undefined,
      });
      toast.success(`Shift ${result.shiftNumber} opened`);
      close(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || "Failed to open shift");
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlayCircle className="h-4 w-4 text-[#4CAF50]" />
            Open shift
          </DialogTitle>
          <DialogDescription>
            Count the cash already in the drawer before you start ringing up
            sales. This is your opening float.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="opening-cash">Opening cash float ({curSymbol()})</Label>
            <Input
              id="opening-cash"
              type="number"
              min="0"
              step="0.01"
              value={openingCash}
              onChange={(e) => setOpeningCash(e.target.value)}
              placeholder="0.00"
              autoFocus
            />
            <p className="text-[11px] text-muted-foreground">
              Enter the total cash physically in the drawer right now. Used to
              compute variance at end of shift.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="opening-notes">Notes (optional)</Label>
            <Textarea
              id="opening-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Anything to remember about how the drawer started…"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={openShift.isPending}
            className="bg-[#4CAF50] hover:bg-[#45A049] text-white"
          >
            {openShift.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                Opening…
              </>
            ) : (
              "Open shift"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
